import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, Eye, Mail, Camera } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useSignedMediaUrl } from '@/hooks/useSignedMediaUrl';

interface VerificationCardProps {
  verification: {
    id: string;
    user_id: string;
    username: string;
    email_verified: boolean;
    selfie_verified: boolean;
    selfie_photo_url: string | null;
    submitted_at: string | null;
    created_at: string;
  };
  type: 'waiting' | 'pending';
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
  isLoading?: boolean;
}

const VerificationCard: React.FC<VerificationCardProps> = ({
  verification,
  type,
  onApprove,
  onReject,
  isLoading = false,
}) => {
  const [showSelfie, setShowSelfie] = useState(false);

  // Obter signed URL para a selfie
  const { signedUrl: signedSelfieUrl, isLoading: loadingSignedUrl } = useSignedMediaUrl({
    mediaUrl: verification.selfie_photo_url || '',
    contentType: 'image'
  });

  // Debug: verificar URL gerada
  useEffect(() => {
    if (signedSelfieUrl) {
      console.log('[VerificationCard] Signed URL gerada:', signedSelfieUrl);
    }
    if (loadingSignedUrl) {
      console.log('[VerificationCard] Carregando signed URL para:', verification.selfie_photo_url);
    }
  }, [signedSelfieUrl, loadingSignedUrl, verification.selfie_photo_url]);

  return (
    <>
      <Card className="bg-black border-white/10 hover:border-primary/50 transition-colors">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white">{verification.username}</h3>
              <p className="text-sm text-white/70">
                {verification.submitted_at
                  ? `Submetido: ${format(new Date(verification.submitted_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`
                  : `Criado: ${format(new Date(verification.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <Badge 
              variant={verification.email_verified ? "default" : "secondary"}
              className={verification.email_verified ? "bg-green-500/20 text-green-400 border-green-500/30" : ""}
            >
              <Mail className="h-3 w-3 mr-1" />
              Email {verification.email_verified ? 'Verificado' : 'Pendente'}
            </Badge>
            <Badge 
              variant={verification.selfie_verified ? "default" : "secondary"}
              className={verification.selfie_verified ? "bg-green-500/20 text-green-400 border-green-500/30" : ""}
            >
              <Camera className="h-3 w-3 mr-1" />
              Selfie {verification.selfie_verified ? 'Verificada' : 'Pendente'}
            </Badge>
          </div>

          {verification.selfie_photo_url && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowSelfie(true)}
              className="w-full mt-2"
            >
              <Eye className="h-4 w-4 mr-2" />
              Visualizar Selfie
            </Button>
          )}
        </CardContent>
      </Card>

      <Dialog open={showSelfie} onOpenChange={setShowSelfie}>
        <DialogContent 
          hasTitle={true} 
          hasDescription={true}
          className="w-[95vw] sm:w-auto max-w-[95vw] sm:max-w-2xl max-h-[90vh] bg-background border-border p-4 sm:p-6"
        >
          <DialogHeader className="space-y-2 sm:space-y-3">
            <DialogTitle className="text-foreground text-base sm:text-lg">
              Verificação - {verification.username}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Painel de verificação de usuário com selfie e documentos
            </DialogDescription>
            <div className="flex flex-wrap gap-2">
              <Badge 
                variant={verification.email_verified ? "default" : "secondary"}
                className={`text-xs ${verification.email_verified ? "bg-green-500/20 text-green-400 border-green-500/30" : ""}`}
              >
                <Mail className="h-3 w-3 mr-1" />
                Email {verification.email_verified ? '✓' : '⏳'}
              </Badge>
              <Badge 
                variant={verification.selfie_verified ? "default" : "secondary"}
                className={`text-xs ${verification.selfie_verified ? "bg-green-500/20 text-green-400 border-green-500/30" : ""}`}
              >
                <Camera className="h-3 w-3 mr-1" />
                Selfie {verification.selfie_verified ? '✓' : '⏳'}
              </Badge>
            </div>
          </DialogHeader>
          
          <div className="flex items-center justify-center p-2 sm:p-4">
            {loadingSignedUrl ? (
              <div className="w-full h-[50vh] sm:h-[60vh] flex items-center justify-center bg-white/5 rounded-lg">
                <div className="text-white/60 text-sm">Carregando...</div>
              </div>
            ) : signedSelfieUrl ? (
              <img
                src={signedSelfieUrl}
                alt={`Selfie de ${verification.username}`}
                className="max-w-full max-h-[50vh] sm:max-h-[60vh] rounded-lg object-contain"
              />
            ) : (
              <div className="w-full h-[50vh] sm:h-[60vh] flex items-center justify-center bg-white/5 rounded-lg">
                <div className="text-white/60 text-sm">Imagem não disponível</div>
              </div>
            )}
          </div>

          {type === 'waiting' && (
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-white/10">
              <Button
                onClick={() => {
                  setShowSelfie(false);
                  onApprove?.(verification.id);
                }}
                disabled={isLoading}
                className="w-full sm:flex-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 border-green-500/30 h-10 sm:h-auto"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Aprovar Verificação</span>
                <span className="sm:hidden">Aprovar</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowSelfie(false);
                  onReject?.(verification.id);
                }}
                disabled={isLoading}
                className="w-full sm:flex-1 bg-destructive/20 hover:bg-destructive/30 text-destructive border-destructive/30 h-10 sm:h-auto"
              >
                <XCircle className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Rejeitar Verificação</span>
                <span className="sm:hidden">Rejeitar</span>
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VerificationCard;
